import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  CircularProgress,
  useTheme,
  InputLabel,
  Tooltip,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  InfoOutlined as InfoIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { gql, useMutation } from '@apollo/client';
import type { Tenant } from './App';
import FailToast from './FailToast';

// --- Icon and GraphQL Definitions ---
const FORM_ICON_URL = `${process.env.PUBLIC_URL}/TenantIcon.svg`;

const CREATE_TENANT_MUTATION = gql`
  mutation CreateTenant($input: CreateTenantInput!) {
    createTenant(input: $input) {
      id
      name
      alias
      tags
      userMemberships {
        id
        roleInTenant
        user {
          id
          name
        }
        addedBy {
          id
          name
        }
        addedAt
        __typename
      }
    }
  }
`;

const SETUP_TENANT_PROVIDER_ACCESS = gql`
  mutation SetupTenantProviderAccess($tenantId: ID!, $providerType: ProviderType!) {
    setupTenantProviderAccess(tenantId: $tenantId, providerType: $providerType) {
      serviceAccountEmail
    }
  }
`;

// --- Interfaces & Enums ---
interface TenantFormProps {
  open: boolean;
  onClose: () => void;
  onTenantCreated: (tenant: Tenant) => void;
  allTenants: Tenant[];
}

interface TagItem {
  id: string;
  key: string;
  value: string;
}

enum ProviderType {
    GCP = "GCP",
    AWS = "AWS",
    Azure = "Azure"
}

// --- Custom icon for the error toast ---
const CustomErrorIcon = () => (
    <Box
      component="span"
      sx={{
        width: 24,
        height: 24,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#D92D20',
      }}
    >
      <CloseIcon sx={{ color: 'white', fontSize: '16px' }} />
    </Box>
);

// --- Component ---
const TenantForm: React.FC<TenantFormProps> = ({ open, onClose, onTenantCreated, allTenants }) => {
  const theme = useTheme();
  const [name, setName] = useState('');
  const [alias, setAlias] = useState('');
  const [tags, setTags] = useState<TagItem[]>([]);
  const [nameError, setNameError] = useState('');
  const [aliasError, setAliasError] = useState('');
  const [tagsError, setTagsError] = useState('');
  
  // State for the error toast
  const [isToastOpen, setIsToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  const [createTenant, { loading: creatingTenant }] = useMutation(CREATE_TENANT_MUTATION);
  const [setupAccess, { loading: settingUpAccess }] = useMutation(SETUP_TENANT_PROVIDER_ACCESS);


  useEffect(() => {
    if (open) {
      setName('');
      setAlias('');
      setTags([]);
      setNameError('');
      setAliasError('');
      setTagsError('');
      setToastMessage('');
    }
  }, [open]);

  // --- Validation functions ---
  const validateName = (currentName: string): string => {
    const trimmedName = currentName.trim();
    if (!trimmedName) return 'Tenant name is required.';
    if (trimmedName.length < 2) return 'Name must be at least 2 characters.';
    if (trimmedName.length > 100) return 'Name cannot exceed 100 characters.';
    const allowedCharsRegex = /^[A-Za-z0-9\s&.,'()-]+$/;
    if (!allowedCharsRegex.test(trimmedName)) return "Name contains invalid characters.";
    if (allTenants.some(tenant => tenant.name.trim().toLowerCase() === trimmedName.toLowerCase())) {
        return 'This tenant name is already taken. Please choose another.';
    }
    return '';
  };
  const validateAlias = (currentAlias: string): string => {
    const trimmedAlias = currentAlias.trim();
    if (!trimmedAlias) return '';
    if (!/^[a-z0-9]+$/.test(trimmedAlias)) return 'Must be alphanumeric and lowercase only.';
    if (trimmedAlias.length > 20) return 'Alias must be 20 characters or less.';
    return '';
  };
  const validateTags = (currentTags: TagItem[]): string => {
    for (const tag of currentTags) {
      if (tag.key.trim()) {
        if (!/^[a-z0-9_-]+$/.test(tag.key)) return `Tag key "${tag.key}" must be alphanumeric lowercase, digits, hyphen, or underscore.`;
        if (tag.key.length > 30) return `Tag key "${tag.key}" must be 30 characters or less.`;
        if (tag.value.trim()){
            if (!/^[a-z0-9_-]+$/.test(tag.value)) return `Tag value for "${tag.key}" must be alphanumeric lowercase, digits, hyphen, or underscore.`;
            if (tag.value.length > 30) return `Tag value for "${tag.key}" must be 30 characters or less.`;
        }
      }
    }
    return '';
  };

  // --- Change Handlers ---
  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setName(val);
    if (nameError) setNameError(validateName(val));
  };
  const handleAliasChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.toLowerCase();
    setAlias(val);
    if (aliasError) setAliasError(validateAlias(val));
  };
  const handleTagChange = (id: string, field: 'key' | 'value', fieldValue: string) => {
    const lowerValue = fieldValue.toLowerCase();
    const updatedTags = tags.map(tag => tag.id === id ? { ...tag, [field]: lowerValue } : tag);
    setTags(updatedTags);
    if (tagsError) setTagsError(validateTags(updatedTags));
  };
  const handleAddTagRow = () => setTags([...tags, { id: Date.now().toString(), key: '', value: '' }]);
  const handleRemoveTagRow = (idToRemove: string) => {
    const remainingTags = tags.filter(tag => tag.id !== idToRemove);
    setTags(remainingTags);
    if (tagsError) setTagsError(validateTags(remainingTags));
  };

  const handleToastClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') return;
    setIsToastOpen(false);
  };

  // --- UPDATED handleSubmit ---
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setNameError('');
    setAliasError('');
    setTagsError('');

    const currentNameError = validateName(name);
    const currentAliasError = validateAlias(alias);
    const currentTagsError = validateTags(tags);

    setNameError(currentNameError);
    setAliasError(currentAliasError);
    setTagsError(currentTagsError);

    if (currentNameError || currentAliasError || currentTagsError) {
      return;
    }

    const tagsObject = tags.reduce((acc, tag) => {
      if (tag.key.trim()) { acc[tag.key.trim()] = tag.value.trim(); }
      return acc;
    }, {} as Record<string, string>);

    try {
      const tenantResult = await createTenant({
        variables: {
          input: {
            name: name.trim(),
            ...(alias.trim() && { alias: alias.trim() }),
            ...(Object.keys(tagsObject).length > 0 && { tags: tagsObject }),
          },
        },
      });
      const newTenant = tenantResult.data?.createTenant;
      if (!newTenant || !newTenant.id) {
        throw new Error("Tenant created, but no ID was returned from the server.");
      }

      await setupAccess({
        variables: {
          tenantId: newTenant.id,
          providerType: ProviderType.GCP,
        },
      });

      onTenantCreated(newTenant);
      onClose();

    } catch (e: any) {
      console.error('Create tenant and setup flow failed:', e);
      setToastMessage("We are unable to add the Tenant at this moment. Please try again after some time.");
      setIsToastOpen(true);
    }
  };

  const loading = creatingTenant || settingUpAccess;
  const isAddButtonDisabled = !!validateName(name) || !!aliasError || !!tagsError || loading;

  const commonTextFieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      '& fieldset': { borderColor: 'rgba(232, 235, 236, 1)' },
      '&:hover fieldset': { borderColor: theme.palette.grey[500] },
    },
    '& .MuiInputBase-input::placeholder': {
        fontSize: '14px', opacity: 1, color: 'rgba(185, 185, 185, 1)', fontFamily: 'Inter'
    },
  };

  const externalLabelSx = {
    mb: 0.5, fontWeight: 450, fontSize: '14px', color: 'rgba(51, 51, 51, 1)', fontFamily: 'Inter'
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        PaperProps={{
          component: 'form',
          onSubmit: handleSubmit,
          sx: {
            borderRadius: '16px',
            maxWidth: '600px',
          }
        }}
        BackdropProps={{ sx: { backdropFilter: 'blur(5px)' } }}
        fullWidth
        sx={{ zIndex: 1301 }}
      >
        <DialogTitle sx={{
          p: theme.spacing(2, 3),
          backgroundImage: `
            linear-gradient(to bottom, transparent 80%, white 100%),
            url('/blue-bg-form.svg')
          `,
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
          backgroundSize: 'cover',
          borderTopRightRadius: '16px',
          borderTopLeftRadius: '16px',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box
                component="span"
                sx={{
                  mt: 3,
                  width: 48,
                  height: 48,
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mr: 1.5,
                  backgroundColor:'#E5EFFF',
                  boxShadow: '0px 1px 2px 0px rgba(10, 13, 18, 0.05)'
                }}
              >
                <img src={FORM_ICON_URL} alt="Form Icon" style={{ width: '24px'}} />
              </Box>
              <Box>
                <Typography
                  variant="h6"
                  component="div"
                  sx={{
                    fontFamily:'Inter',
                    fontWeight: 600,
                    fontSize: '16px',
                    mb:0.3,
                    mt: 3,
                    color: 'rgba(3, 23, 52, 1)'
                  }}
                >
                  Add New Tenant
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    fontFamily:'Inter',
                    fontSize: '14px',
                    fontWeight: 400,
                    color: 'rgba(71, 84, 103, 1)'
                  }}
                >
                  Fill below details to add new tenant.
                </Typography>
              </Box>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 2, sm: 2 }, mb: 2.5, mt: 5 }}>
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Tooltip title="Name must be 2-100 characters. Allowed special characters: & . , ' - ( )" placement="top-start" arrow>
                <InputLabel htmlFor="name" sx={externalLabelSx}>
                  Tenant Name (Company Name) <span style={{ color: theme.palette.error.main }}>*</span>
                </InputLabel>
              </Tooltip>
              <TextField
                autoFocus
                required
                id="name"
                type="text"
                fullWidth
                variant="outlined"
                size="small"
                value={name}
                onChange={handleNameChange}
                onBlur={() => { if (name) setNameError(validateName(name)); }}
                disabled={loading}
                placeholder="Enter company's full name"
                sx={commonTextFieldSx}
                error={!!nameError}
                helperText={nameError}
              />
            </Box>

            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Tooltip title="Optional. Lowercase alphanumeric, max 20 chars." placement="top-start" arrow>
                <InputLabel htmlFor="alias" sx={externalLabelSx}>
                  Internal Alias
                </InputLabel>
              </Tooltip>
              <TextField
                id="alias"
                type="text"
                fullWidth
                variant="outlined"
                size="small"
                value={alias}
                onChange={handleAliasChange}
                disabled={loading}
                placeholder="Enter alias (e.g., apac)"
                sx={commonTextFieldSx}
                error={!!aliasError}
                helperText={aliasError}
              />
            </Box>
          </Box>

          <Box sx={{ mt: 1, mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Tooltip
                title="Use tags to organize costs by categories like department or project. Keys and values must be 30 characters or less and contain only lowercase letters, numbers, hyphens (-), and underscores (_)."
                placement="top-start"
                arrow
                PopperProps={{
                  modifiers: [
                    {
                      name: 'offset',
                      options: {
                        offset: [34, 0],
                      },
                    },
                  ],
                }}
              >
                <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 450, fontSize: '14px', fontFamily:'Inter' }}>
                    Tags
                  </Typography>
                  <InfoIcon color="action" sx={{ fontSize: '1rem', ml: 0.5, verticalAlign: 'middle', color:'#0064FF' }} />
                </Box>
              </Tooltip>
               <Button onClick={handleAddTagRow} disabled={loading} variant="text" color="primary" size="small" sx={{ textTransform: 'none', fontWeight: 450, fontFamily:'Inter', color:'#0064FF', fontSize:'14px' }}>
                 + Add Tag
               </Button>
             </Box>

             {tags.map((tag) => (
              <Box key={tag.id} sx={{ display: 'flex', alignItems: 'flex-start', gap: {xs: 2, sm: 1.5}, mb: 1.5, flexDirection: {xs: 'column', sm: 'row'} } }>
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', width: '100%' }}>
                  <InputLabel htmlFor={`tag-key-${tag.id}`} sx={externalLabelSx}> Key </InputLabel>
                  <TextField
                    id={`tag-key-${tag.id}`}
                    placeholder="E.g. department"
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={tag.key}
                    onChange={(e) => handleTagChange(tag.id, 'key', e.target.value)}
                    disabled={loading}
                    sx={commonTextFieldSx}
                  />
                </Box>
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', width: '100%' }}>
                  <InputLabel htmlFor={`tag-value-${tag.id}`} sx={externalLabelSx}> Value </InputLabel>
                  <TextField
                    id={`tag-value-${tag.id}`}
                    placeholder="E.g. production"
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={tag.value}
                    onChange={(e) => handleTagChange(tag.id, 'value', e.target.value)}
                    disabled={loading}
                    sx={commonTextFieldSx}
                  />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', pt: {xs: 0, sm: '20px'}, ml: {xs: 'auto', sm: 0} }}>
                  <IconButton
                      onClick={() => handleRemoveTagRow(tag.id)}
                      disabled={loading}
                      aria-label="Delete tag"
                      size="medium"
                  >
                    <img
                      src={process.env.PUBLIC_URL + '/delete-bin-icon.svg'}
                      alt="Delete tag"
                      style={{ width: '20px', height: '20px' }}
                    />
                  </IconButton>
                </Box>
              </Box>
            ))}
            {tagsError && (
              <Typography color="error" variant="caption" sx={{ mt: 1, display: 'block', textAlign:'left' }}>
                {tagsError}
              </Typography>
            )}
          </Box>

        </DialogContent>
        <DialogActions sx={{ p: theme.spacing(2, 3) }}>
          <Button
            onClick={onClose}
            variant="outlined"
            disabled={loading}
            sx={{
              width: '120px',
              borderRadius: '8px', color: 'rgba(52, 64, 84, 1)', borderColor: 'rgba(208, 213, 221, 1)',
              '&:hover': { borderColor: theme.palette.grey[700], backgroundColor: 'action.hover' },
              py: '8px', textTransform: 'none', fontWeight: 500, fontSize: '14px', fontFamily:'Inter',
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={isAddButtonDisabled}
            sx={{
              width: '120px',
              py: '8px', borderRadius:'8px', textTransform: 'none',
              fontWeight: 500, fontSize: '14px', fontFamily:'Inter', backgroundColor: '#0064FF',
              '&.Mui-disabled': {
                  backgroundColor: 'rgba(224, 224, 224, 1)',
                  color: 'rgba(173, 173, 173, 1)'
              }
            }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Add Tenant'}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={isToastOpen}
        autoHideDuration={6000}
        onClose={handleToastClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          icon={<CustomErrorIcon />} 
          onClose={handleToastClose}
          sx={{
            width: '100%',
            borderRadius: '50px',
            backgroundColor: 'white',
            boxShadow: theme.shadows[3],
            color: 'text.secondary',
            alignItems: 'center',
            p: theme.spacing(0.5, 2),
            '& .MuiAlert-message': {
              flex: 1,
              py: '12px'
            },
          }}
        >
          {toastMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default TenantForm;
